import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  Box, Columns, DashboardModule, LoadingModule, ModalContext, PageHeader,
} from '@campaignhub/suit-theme'

import { useModals, useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'

import useReduxAction from '@hooks/useReduxAction'
import useUsersV4 from '@hooks/useUsersV4'

import EditUserModal from '@modals/EditUserModalV4'

import blankState from '@components/assets/blankState.svg'
import custom from '@styles/custom.module.scss'
import Tools from './components/Tools'
import User from './components/User'

const deleteUser = (deleteFn, setState) => {
  deleteFn().then(({ success, errors }) => {
    if (!success && errors) {
      toast.warn(errors[0])
      return
    }

    setState({ showEditUserModal: false })
  })
}

const updateUser = (userParams, updateFn, setState) => {
  updateFn(userParams).then(({ success, errors }) => {
    if (!success && errors) {
      toast.warning(errors[0])
      return
    }

    setState({ showEditUserModal: false })
  })
}

const callbacks = (component, setState) => {
  const componentCallbacks = {
    EditUserModal: {
      closeModal: () => setState({ showEditUserModal: false }),
      deleteUser: deleteFn => deleteUser(deleteFn, setState),
      updateUser: (userParams, updateFn) => updateUser(userParams, updateFn, setState),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  defaultCount: 50,
  loadNext: false,
  page: 0,
  refresh: false,
  searchFilter: '',
  selectedEmployment: [],
  selectedRole: [],
  selectedService: [],
  showEditUserModal: false,
}

const UserList = () => {
  const [state, setState] = useSetState(defaultState)
  const { selectedRole, searchFilter, showEditUserModal,
    selectedService, selectedEmployment, refresh, defaultCount, page, loadNext } = state

  const { result: userRolesV4 } = useSelector(reduxState => reduxState.userRolesV4)
  const entityReducer = useSelector(reduxState => reduxState.usersV4)
  const { result : { hasMore }, loading } = entityReducer
  const rolesLoaded = Object.values(userRolesV4)?.length > 0
  const isLoading = loading || !rolesLoaded
  
  const defaultRoles = Object.values(userRolesV4)
    ?.filter(x => !['Agent', 'Assistant', 'Client_Admin', 'Unknown'].includes(x?.value))
    ?.map(x => x.value) || []

  const roles = selectedRole.length > 0 ? selectedRole.join(',') : defaultRoles.join(',')

  const options = {
    size: defaultCount,
    sort: 'name asc',
    offset: page * defaultCount,
    loadNext,
    name: searchFilter,
    ...roles.length > 0 && { 'roles': roles },
    ...selectedService.length > 0 && { 'services': selectedService.join(',') },
    ...selectedEmployment.length > 0 && { 'employment': selectedEmployment.join(',') },
  }

  useReduxAction('usersV4', 'loadUsersV4', options, [searchFilter, selectedService, selectedRole, selectedEmployment, page, userRolesV4], {
    shouldPerformFn: (entityReducer) => {
      const { loading } = entityReducer
      return !loading && Object.values(userRolesV4)?.length > 0
    },
  })

  const { filteredUsersV4 } = useUsersV4()

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const updateSearchFilter = (val) => {
    setState({ searchFilter: val, page: 0, loadNext: false })
  }

  const updateSelectedService = (checked, val) => {
    if (checked) {
      setState({ selectedService: [...selectedService, val], page: 0, loadNext: false })
    } else {
      setState({ selectedService: selectedService.filter(i => i !== val), page: 0, loadNext: false })
    }
  }

  const updateSelectedRole = (checked, val) => {
    if (checked) {
      setState({ selectedRole: [...selectedRole, val], page: 0, loadNext: false })
    } else {
      setState({ selectedRole: selectedRole.filter(i => i !== val), page: 0, loadNext: false })
    }
  }

  const updateSelectedEmployment = (checked, val) => {
    if (checked) {
      setState({ selectedEmployment: [...selectedEmployment, val], page: 0, loadNext: false })
    } else {
      setState({ selectedEmployment: selectedEmployment.filter(i => i !== val), page: 0, loadNext: false })
    }
  }

  const resetFilter = () => {
    setState({ searchFilter: '', selectedRole: [], selectedService: [], selectedEmployment: [], page: 0, loadNext: false })
  }

  const loadMore = () => {
    setState({ page: page + 1, loadNext: true })
  }

  const pageContext = {
    callbacks: {
      showEditUserModal: (payload) => {
        setModalData('EditUserModal', payload)
        setState({ showEditUserModal: true })
      },
      toggleResetFilter: () => resetFilter(),
      toggleSelectedService: (checked, val) => updateSelectedService(checked, val),
      toggleSelectedRole: (checked, val) => updateSelectedRole(checked, val),
      toggleSelectedEmployment: (checked, val) => updateSelectedEmployment(checked, val),
      loadMore,
      searchNameFilter: val => updateSearchFilter(val),
    },
    selectedService,
    selectedRole,
    selectedEmployment,
    searchFilter,
    refresh,
  }

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageHeader
          activeTabBarItemKey="users"
          boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
          title="User Management"
          offset={{ left: 70 }}
        />
        <Box paddingX="large" paddingTop={[112, 105]}>
          <Columns
            boxProps={{ marginTop: 'large' }}
            flexDirection={['column', 'column', 'row']}
          >
            <Columns.Main>
              <DashboardModule title="Users">
                <Box flexDirection="column" className={custom.scroll}>
                  <Box flexDirection="column" maxHeight="75vh" overflowY="auto">
                    {filteredUsersV4.length > 0 && filteredUsersV4.map(user => (
                      <User key={user.id} user={user} />
                    ))}
                    <LoadingModule loading={isLoading} boxProps={{ border: 0 }} />
                  </Box>
                  {filteredUsersV4.length > 0 && (
                    <DashboardModule.LoadMoreFooter
                      callbacks={{ loadMore: () => loadMore() }}
                      canLoadMore={hasMore}
                      entityCount={filteredUsersV4?.length}
                    />
                  )}
                  {!isLoading && !filteredUsersV4.length && (
                    <DashboardModule.BlankState imageUrl={blankState}>
                      <DashboardModule.BlankState.Title>No Users Available</DashboardModule.BlankState.Title>
                      <DashboardModule.BlankState.Paragraph>
                        Try adjusting your filters.
                      </DashboardModule.BlankState.Paragraph>
                    </DashboardModule.BlankState>
                  )}
                </Box>
              </DashboardModule>
            </Columns.Main>
            <Columns.Sidebar>
              <DashboardModule title="Tools">
                <Tools />
              </DashboardModule>
            </Columns.Sidebar>
          </Columns>
        </Box>
        <EditUserModal
          callbacks={callbacks('EditUserModal', setState)}
          showModal={showEditUserModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default UserList
